import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getFeed, submitRating, FeedUser } from '../api';
import UserCard from '../components/UserCard';
import RatingSlider from '../components/RatingSlider';
import './Rating.css';

const Rating: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<FeedUser[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState<number | null>(null);
  const [showHint, setShowHint] = useState(false);
  const hintTimerRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!user?.profileComplete) {
      navigate('/complete-profile');
      return;
    }
    loadFeed();
  }, [user, navigate]);

  useEffect(() => {
    // Show rating hint after 3 seconds of inactivity
    if (users.length > 0 && currentIndex < users.length) {
      hintTimerRef.current = setTimeout(() => {
        setShowHint(true);
      }, 3000);

      return () => {
        if (hintTimerRef.current) {
          clearTimeout(hintTimerRef.current);
        }
      };
    }
    return undefined;
  }, [currentIndex, users]);

  const loadFeed = async () => {
    try {
      const response = await getFeed(10, 0);
      setUsers(response.data.users);
    } catch (error) {
      console.error('Failed to load feed:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    try {
      const response = await getFeed(10, users.length);
      setUsers((prev) => [...prev, ...response.data.users]);
    } catch (error) {
      console.error('Failed to load more users:', error);
    }
  };

  const handleRating = async (ratingValue: number) => {
    if (currentIndex >= users.length) return;

    const currentUser = users[currentIndex];
    setRating(ratingValue);
    setShowHint(false);

    try {
      await submitRating({
        ratedUserId: currentUser.id,
        rating: ratingValue,
        photoVersionSeen: currentUser.photoVersion,
      });

      // Move to next user after brief delay
      setTimeout(() => {
        setRating(null);
        setCurrentIndex((prev) => prev + 1);

        // Load more if needed
        if (currentIndex + 1 >= users.length - 3) {
          loadMore();
        }
      }, 500);
    } catch (error) {
      console.error('Failed to submit rating:', error);
      setRating(null);
    }
  };

  const handleCancel = () => {
    setRating(null);
    setShowHint(false);
  };

  if (loading) {
    return (
      <div className="rating-container loading">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (users.length === 0 || currentIndex >= users.length) {
    return (
      <div className="rating-container empty">
        <div className="empty-state">
          <h2>No More Users to Rate</h2>
          <p>Check back later or search for specific users</p>
          <button onClick={() => navigate('/profile')}>Go to Profile</button>
        </div>
      </div>
    );
  }

  return (
    <div className="rating-container">
      <div className="rating-header">
        <h1 className="app-name">Lilt</h1>
        <button className="profile-btn" onClick={() => navigate('/profile')}>
          Profile
        </button>
      </div>

      <div className="card-container">
        <UserCard
          user={users[currentIndex]}
          rating={rating}
          onCancel={handleCancel}
        />
      </div>

      <RatingSlider
        onRate={handleRating}
        showHint={showHint}
        disabled={rating !== null}
      />

      <div className="progress-indicator">
        {currentIndex + 1} / {users.length}
      </div>
    </div>
  );
};

export default Rating;
